import React, { useMemo, useState } from 'react';

import { CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import { ApplicationUuid, DeliveryBlockResource, ParkingSpot, ServiceArea, Vehicle } from '@bringg/types';
import { Button } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import {
	DeliveryBlockResourceLocationType,
	DeliveryBlockResourceServiceArea
} from '@bringg/types/types/delivery_block';

import { useStores } from '../../../../recipes';
import {
	DriversSelect,
	getLocationsSelect,
	ServiceAreaSelect,
	VehiclesSelect
} from '../resources/select-options-provider';
import { UseResourceTable } from '../../../../hooks/use-resource-table';
import Driver from '../../../../stores/drivers/domain-object/driver';

export const UNKNOWN_ID = null;

export enum COLUMN_ACCESSORS {
	DRIVER_NAME = 'driverName',
	VEHICLE_NAME = 'vehicleName',
	TRAILER_NAME = 'trailerName',
	SERVICE_AREA_NAME = 'serviceAreas',
	AVOID_AREA_NAME = 'avoidAreas',
	START_LOCATION = 'startLocation',
	END_LOCATION = 'endLocation',
	REMOVE_RESOURCE = 'removeResource'
}

export interface DefaultLocation {
	id: number;
	name: string;
	defaultLocation: boolean;
}

export interface ParkingSpotLocation extends Partial<ParkingSpot> {
	defaultLocation: boolean;
}

export interface ConfiguredDefaultLocation {
	id: number;
	isFallback: boolean;
}

export type SortItem = {
	id: string;
	desc_direction: boolean;
};

const useResources = (
	drivers: Map<number, Driver>,
	vehicles: Map<number, Vehicle>,
	trailers: Map<number, Vehicle>,
	serviceAreas: ServiceArea[],
	teamId: number,
	resources: DeliveryBlockResource[],
	parkingSpots: ParkingSpot[],
	selectedVehiclesIds: Set<number>,
	selectedDriverIds: Set<number>,
	selectedTrailersIds: Set<number>,
	deliveryBlockTimeRange: { startTime: string; endTime: string },
	unavailableDriverIds: Set<number>,
	unavailableVehicleIds: Set<number>,
	unavailableTrailerIds: Set<number>,
	onResourceChanged?: (resource: DeliveryBlockResource) => void,
	removeResource?: (resource: DeliveryBlockResource) => void
) => {
	const { applicationMerchantConfigurationStore, applicationTeamConfigurationStore, vehicleTypesStore } = useStores();
	const { t } = useTranslation();
	const [sortByColumn, setSortByColumn] = useState<SortItem>();
	const { getResourceColumns } = UseResourceTable(resources, {
		showTrailersColumn: !!trailers.size,
		showDriversColumn: !!drivers.size,
		showServiceAreasColumn: !!serviceAreas.length
	});

	const defaultLocations = useMemo(() => {
		const teamConfig = applicationTeamConfigurationStore.getConfigData(ApplicationUuid.RouteOptimizer2, teamId);
		const merchantConfig = applicationMerchantConfigurationStore.getConfiguration(ApplicationUuid.RouteOptimizer2);
		return {
			startLocation:
				teamConfig?.default_start_location ||
				merchantConfig?.default_start_location ||
				DeliveryBlockResourceLocationType.TEAM_ADDRESS,
			endLocation:
				teamConfig?.default_end_location ||
				merchantConfig?.default_end_location ||
				DeliveryBlockResourceLocationType.LAST_DRIVER_LOCATION
		};
	}, [
		applicationMerchantConfigurationStore.configurationsData,
		applicationTeamConfigurationStore.configurationsData
	]);

	const UNKNOWN_VEHICLE = {
		id: UNKNOWN_ID,
		name: t('DELIVERY_BLOCKS.RESOURCES.NO_VEHICLE')
	} as Vehicle;

	const UNKNOWN_TRAILER = {
		id: UNKNOWN_ID,
		name: t('DELIVERY_BLOCKS.RESOURCES.NO_TRAILER')
	} as Vehicle;

	const UNKNOWN_DRIVER = {
		id: UNKNOWN_ID,
		name: t('DELIVERY_BLOCKS.RESOURCES.NO_DRIVER')
	} as Driver;

	const getDrivers = (): Map<number, Driver> => {
		const driversMap = new Map();
		driversMap.set(UNKNOWN_DRIVER.id, UNKNOWN_DRIVER);
		drivers.forEach((value, key) => {
			driversMap.set(key, value);
		});
		return driversMap;
	};

	const getVehiclesWithoutTrailers = () => {
		const vehiclesWithoutTrailersMap = new Map<number, Vehicle>([...getVehicles()]);
		Array.from(vehiclesWithoutTrailersMap.values()).forEach((vehicle: Vehicle) => {
			if (vehicle.is_trailer) {
				vehiclesWithoutTrailersMap.delete(vehicle.id);
			}
		});

		return vehiclesWithoutTrailersMap;
	};

	const getVehicles = () => {
		const vehiclesMap = new Map();
		vehiclesMap.set(UNKNOWN_VEHICLE.id, UNKNOWN_VEHICLE);
		vehicles.forEach((value, key) => {
			vehiclesMap.set(key, value);
		});
		return vehiclesMap;
	};

	const getTrailers = () => {
		const trailersMap = new Map();
		trailersMap.set(UNKNOWN_TRAILER.id, UNKNOWN_TRAILER);
		trailers.forEach((value, key) => {
			trailersMap.set(key, value);
		});
		return trailersMap;
	};

	const getServiceAreas = () => {
		const serviceAreasMap = new Map();
		serviceAreas.forEach(value => {
			serviceAreasMap.set(value.id, value);
		});
		return serviceAreasMap;
	};

	const hasHomeAddress = (driverId?: number): boolean => {
		return driverId && !!drivers.get(driverId)?.home_address;
	};

	const hasParkingLocation = (vehicleId?: number): boolean => {
		return vehicleId && !!vehicles.get(vehicleId)?.default_parking_spot_id;
	};

	const isResourcePredefinedLocation = (location?: number): boolean => {
		return [
			DeliveryBlockResourceLocationType.DRIVER_ADDRESS,
			DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING
		].includes(location);
	};

	const getResourceSelectedAreas = (resourceServiceAreas: DeliveryBlockResourceServiceArea[]) => {
		const initalValues = { selectedAvoidAreas: [], selectedServiceAreas: [] };

		return resourceServiceAreas
			? resourceServiceAreas.reduce((selectedAreas, serviceArea) => {
					const key = serviceArea.avoid ? 'selectedAvoidAreas' : 'selectedServiceAreas';
					selectedAreas[key].push(serviceArea.service_area_id);
					return selectedAreas;
			  }, initalValues)
			: initalValues;
	};

	const addOrRemoveServiceAreaFromResource = (
		id: number,
		avoid: boolean,
		originalResource: DeliveryBlockResource,
		name?: string
	) => {
		const resource = resources.find(item => item === originalResource);
		resource.service_areas = resource.service_areas || [];
		const serviceAreaIndex = resource.service_areas.findIndex(
			serviceArea => serviceArea.service_area_id === id && serviceArea.avoid === avoid
		);
		if (serviceAreaIndex < 0) {
			const serviceArea = { service_area_id: id, avoid, name };
			resource.service_areas.push(serviceArea);
		} else {
			resource.service_areas.splice(serviceAreaIndex, 1);
		}
		if (onResourceChanged) {
			onResourceChanged(resource);
		}
	};

	const getDefaultLocation = (
		isStartLocation: boolean,
		originalResource: DeliveryBlockResource
	): ConfiguredDefaultLocation => {
		const driverId = originalResource.user_id;
		const vehicleId = originalResource.vehicle_id;
		const location = isStartLocation ? defaultLocations.startLocation : defaultLocations.endLocation;

		if (
			(location !== DeliveryBlockResourceLocationType.DRIVER_ADDRESS || hasHomeAddress(driverId)) &&
			(location !== DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING || hasParkingLocation(vehicleId))
		) {
			return { id: location, isFallback: false };
		}
		const teamConfig = applicationTeamConfigurationStore.getConfigData(ApplicationUuid.RouteOptimizer2, teamId);
		const merchantConfig = applicationMerchantConfigurationStore.getConfiguration(ApplicationUuid.RouteOptimizer2);
		const field = `default_${isStartLocation ? 'start' : 'end'}_location`;
		const fallbackLocation = isStartLocation
			? DeliveryBlockResourceLocationType.TEAM_ADDRESS
			: DeliveryBlockResourceLocationType.LAST_DRIVER_LOCATION;
		if (teamConfig && teamConfig[field] && !isResourcePredefinedLocation(teamConfig[field])) {
			return teamConfig[field];
		}
		if (merchantConfig && merchantConfig[field] && !isResourcePredefinedLocation(merchantConfig[field])) {
			return merchantConfig[field];
		}
		return { id: fallbackLocation, isFallback: true };
	};

	const getLocations = (isStartLocation: boolean): Map<number, ParkingSpotLocation | DefaultLocation> => {
		const driverLocation = {
			id: DeliveryBlockResourceLocationType.DRIVER_ADDRESS,
			name: t('DELIVERY_BLOCKS.RESOURCES.DRIVER_HOME'),
			defaultLocation: true
		} as DefaultLocation;
		const teamLocation = {
			id: DeliveryBlockResourceLocationType.TEAM_ADDRESS,
			name: t('DELIVERY_BLOCKS.TEAM_LOCATION'),
			defaultLocation: true
		} as DefaultLocation;
		const vehicleDefaultParkingLocation = {
			id: DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING,
			name: t('DELIVERY_BLOCKS.VEHICLE_PARKING'),
			defaultLocation: true
		} as DefaultLocation;
		const lastDriverLocation = {
			id: DeliveryBlockResourceLocationType.LAST_DRIVER_LOCATION,
			name: t('DELIVERY_BLOCKS.LAST_DRIVER_LOCATION'),
			defaultLocation: true
		} as DefaultLocation;

		const baseLocations = [driverLocation, teamLocation, vehicleDefaultParkingLocation, lastDriverLocation];

		const defaultLocationName = baseLocations.find(location => {
			const id = isStartLocation ? defaultLocations.startLocation : defaultLocations.endLocation;
			return location.id === id;
		}).name;

		const defaultLocation = {
			id: UNKNOWN_ID,
			name: t('DELIVERY_BLOCKS.RESOURCES.DEFAULT', { default_location: defaultLocationName }),
			defaultLocation: true
		} as DefaultLocation;

		const locationsArray = [defaultLocation, driverLocation, teamLocation, vehicleDefaultParkingLocation];

		if (!isStartLocation) {
			locationsArray.push(lastDriverLocation);
		}
		locationsArray.push(...parkingSpots.map(parkingSpot => Object.assign(parkingSpot, { defaultLocation: false })));

		const locationsMap = new Map<number, ParkingSpotLocation>();
		locationsArray.forEach(location => {
			locationsMap.set(location.id, location);
		});
		return locationsMap;
	};

	const getTableData = useMemo(() => {
		const startLocations: Map<number, Partial<ParkingSpot>> = getLocations(true);
		const endLocations: Map<number, Partial<ParkingSpot>> = getLocations(false);
		const driversById: Map<number, Partial<Driver>> = getDrivers();
		const vehiclesById: Map<number, Partial<Vehicle>> = getVehicles();
		const trailersById: Map<number, Partial<Vehicle>> = getTrailers();
		const serviceAreasById: Map<number, Partial<ServiceArea>> = getServiceAreas();
		return resources
			.map(resource => {
				const startLocation = startLocations.get(
					resource.start_parking_spot_id ?? resource.start_location_type
				);
				const endLocation = endLocations.get(resource.end_parking_spot_id ?? resource.end_location_type);
				const driverName = driversById.get(resource.user_id) ?? UNKNOWN_DRIVER;
				const vehicleName = vehiclesById.get(resource.vehicle_id) ?? UNKNOWN_VEHICLE;
				const trailerName = trailersById.get(resource.trailer_id) ?? UNKNOWN_TRAILER;
				const serviceAreaId = resource.service_areas?.find(sa => !sa.avoid)?.service_area_id;
				const avoidAreaId = resource.service_areas?.find(sa => sa.avoid)?.service_area_id;
				const serviceAreaName = serviceAreasById.get(serviceAreaId);
				const avoidAreaName = serviceAreasById.get(avoidAreaId);

				return {
					startLocation,
					endLocation,
					driverName,
					vehicleName,
					trailerName,
					serviceAreas: serviceAreaName,
					avoidAreas: avoidAreaName,
					resource
				};
			})
			.sort((a, b) => {
				if (!sortByColumn) {
					return 0;
				}
				const aObj = a[sortByColumn.id];
				const bObj = b[sortByColumn.id];

				if (!aObj && !bObj) {
					return 0;
				}
				if (!aObj && bObj) {
					return 1;
				}
				if (!bObj && aObj) {
					return 1;
				}

				const result = aObj.name.localeCompare(bObj.name);
				return sortByColumn.desc_direction ? result * -1 : result;
			});
	}, [resources, sortByColumn]);

	const setResourceField = (originalResource: DeliveryBlockResource, id: number, fieldName: string) => {
		const resource = resources.find(item => item === originalResource);
		resource[fieldName] = id;

		if (fieldName === 'user_id') {
			if (resource.start_location_type === DeliveryBlockResourceLocationType.DRIVER_ADDRESS) {
				resource.start_location_type = UNKNOWN_ID;
			} else if (resource.end_location_type === DeliveryBlockResourceLocationType.DRIVER_ADDRESS) {
				resource.end_location_type = UNKNOWN_ID;
			}
		} else if (fieldName === 'vehicle_id') {
			if (resource.start_location_type === DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING) {
				resource.start_location_type = UNKNOWN_ID;
			} else if (resource.end_location_type === DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING) {
				resource.end_location_type = UNKNOWN_ID;
			}

			const vehicle = vehicles.get(id);
			const vehicleType = vehicleTypesStore.get(vehicle?.vehicle_type_id);
			// @ts-ignore
			const trailerIdAttachToVehicle = vehicle?.trailer?.id;
			if (trailerIdAttachToVehicle) {
				resource.use_default_trailer = true;
				resource.trailer_id = null;
			} else {
				resource.use_default_trailer = false;
			}

			if (!vehicleType?.can_attach_trailer) {
				resource.use_default_trailer = false;
				resource.trailer_id = null;
			}
		} else if (fieldName === 'trailer_id') {
			resource.use_default_trailer = false;
		}
		if (onResourceChanged) {
			onResourceChanged(resource);
		}
	};

	const COLUMN_WIDTH = serviceAreas.length ? 130 : 150;
	const withServiceAreas = !!serviceAreas.length;

	const getTableColumns = useMemo(() => {
		const startLocations = getLocations(true);
		const endLocations = getLocations(false);

		const onHeaderClick = (id: string) => {
			setSortByColumn((prev: SortItem) => {
				if (prev?.id === id) {
					return { id, desc_direction: !prev.desc_direction };
				} else {
					return { id, desc_direction: true };
				}
			});
		};

		return [
			...getResourceColumns().map(column => {
				let cell;

				if (column.accessor === COLUMN_ACCESSORS.DRIVER_NAME) {
					cell = ({ row, value }: CellProps<any>) => {
						const defaultDriverId = resources.find(
							resource => resource.id === row.original.resource.id
						)?.user_id;
						return (
							<DriversSelect
								drivers={getDrivers()}
								selectedDriverIds={selectedDriverIds}
								value={value}
								onSelect={option => setResourceField(row.original.resource, option, 'user_id')}
								defaultId={defaultDriverId}
								tooltipText={t('DELIVERY_BLOCKS.DRIVER_NOT_AVAILABLE_AT_THIS_TIME')}
								unavailableDriverIds={unavailableDriverIds}
								withServiceAreas={withServiceAreas}
							/>
						);
					};
				} else if (column.accessor === COLUMN_ACCESSORS.VEHICLE_NAME) {
					cell = ({ row, value }: CellProps<any>) => {
						const foundResource = resources.find(
							resource => resource.vehicle_id === row.original.resource.vehicle_id
						);
						const defaultVehicleId = foundResource?.vehicle_id;

						return (
							<VehiclesSelect
								vehicles={getVehiclesWithoutTrailers()}
								selectedVehicleIds={selectedVehiclesIds}
								value={value}
								onSelect={option => setResourceField(row.original.resource, option, 'vehicle_id')}
								defaultId={defaultVehicleId}
								translations={{ tooltipText: t('DELIVERY_BLOCKS.VEHICLE_NOT_AVAILABLE_AT_THIS_TIME') }}
								unavailableVehicleIds={unavailableVehicleIds}
								unavailableTrailerIds={unavailableTrailerIds}
								itemClassName="vehicle-item-text"
								withServiceAreas={withServiceAreas}
							/>
						);
					};
				} else if (column.accessor === COLUMN_ACCESSORS.TRAILER_NAME) {
					cell = ({ row, value }: CellProps<any>) => {
						const foundResource = resources.find(
							resource => resource.vehicle_id === row.original.resource.vehicle_id
						);
						const vehicle = vehicles.get(foundResource.vehicle_id);
						const canAttachTrailer = vehicleTypesStore.get(vehicle?.vehicle_type_id)?.can_attach_trailer;

						let trailerId = foundResource.trailer_id;
						if (foundResource.use_default_trailer) {
							// @ts-ignore
							trailerId = vehicle?.trailer?.id;
						}
						return (
							<VehiclesSelect
								vehicles={getTrailers()}
								selectedVehicleIds={selectedTrailersIds}
								value={trailers.get(trailerId) || value}
								onSelect={option => setResourceField(row.original.resource, option, 'trailer_id')}
								defaultId={trailerId}
								translations={{
									tooltipText: t('DELIVERY_BLOCKS.VEHICLE_NOT_AVAILABLE_AT_THIS_TIME'),
									defaultTrailer: t('DELIVERY_BLOCKS.DEFAULT_TRAILER'),
									defaultText: t('DELIVERY_BLOCKS.RESOURCES.DEFAULT_TEXT')
								}}
								unavailableVehicleIds={unavailableVehicleIds}
								unavailableTrailerIds={unavailableTrailerIds}
								isDisabled={!canAttachTrailer}
								trailerIdAttachedToVehicle={vehicle?.trailer?.id}
								itemClassName="trailer-item-text"
								withServiceAreas={withServiceAreas}
							/>
						);
					};
				} else if (withServiceAreas && column.accessor === COLUMN_ACCESSORS.SERVICE_AREA_NAME) {
					cell = ({ row }: CellProps<any>) => {
						const rowServiceAreas = row.original.resource.service_areas;
						const { selectedAvoidAreas, selectedServiceAreas } = getResourceSelectedAreas(rowServiceAreas);

						return (
							<ServiceAreaSelect
								serviceAreas={serviceAreas}
								selectedServiceAreas={selectedServiceAreas}
								unavailableAreas={selectedAvoidAreas}
								addServiceArea={(id, name) =>
									addOrRemoveServiceAreaFromResource(id, false, row.original.resource, name)
								}
								removeServiceArea={id =>
									addOrRemoveServiceAreaFromResource(id, false, row.original.resource)
								}
								placeholder={t('DELIVERY_BLOCKS.SERVICE_AREA_PLACEHOLDER')}
								valueText={t('DELIVERY_BLOCKS.AREAS')}
								prefix="service-areas"
							/>
						);
					};
				} else if (withServiceAreas && column.accessor === COLUMN_ACCESSORS.AVOID_AREA_NAME) {
					cell = ({ row }: CellProps<any>) => {
						const rowServiceAreas = row.original.resource.service_areas;
						const { selectedAvoidAreas, selectedServiceAreas } = getResourceSelectedAreas(rowServiceAreas);

						return (
							<ServiceAreaSelect
								serviceAreas={serviceAreas}
								selectedServiceAreas={selectedAvoidAreas}
								unavailableAreas={selectedServiceAreas}
								addServiceArea={(id, name) =>
									addOrRemoveServiceAreaFromResource(id, true, row.original.resource, name)
								}
								removeServiceArea={id =>
									addOrRemoveServiceAreaFromResource(id, true, row.original.resource)
								}
								placeholder={t('DELIVERY_BLOCKS.SERVICE_AREA_PLACEHOLDER')}
								valueText={t('DELIVERY_BLOCKS.AREAS')}
								prefix="avoid-areas"
							/>
						);
					};
				}
				const width = COLUMN_WIDTH;

				if (cell) {
					Object.assign(column, { Cell: cell });
				}
				if (width) {
					Object.assign(column, { width });
				}
				return column;
			}),
			...[
				{
					id: COLUMN_ACCESSORS.START_LOCATION,
					Header: t('RESOURCES_TABLE.START_LOCATION'),
					accessor: COLUMN_ACCESSORS.START_LOCATION,
					filter: 'byName',
					Cell: ({ row, value }: CellProps<any>) =>
						getLocationsSelect(
							startLocations,
							row,
							value,
							getDefaultLocation(true, row.original.resource),
							hasHomeAddress,
							hasParkingLocation,
							option => {
								const item: ParkingSpotLocation | DefaultLocation = startLocations.get(option);
								if (item.defaultLocation) {
									setResourceField(row.original.resource, option, 'start_location_type');
									setResourceField(row.original.resource, null, 'start_parking_spot_id');
								} else {
									setResourceField(
										row.original.resource,
										DeliveryBlockResourceLocationType.PARKING_SPOT,
										'start_location_type'
									);
									setResourceField(row.original.resource, option, 'start_parking_spot_id');
								}
							},
							t('DELIVERY_BLOCK_MODAL.LOCATIONS_LIST'),
							t('DELIVERY_BLOCK_MODAL.OTHER_LOCATIONS_LIST'),
							'start-location-item-text'
						)
				},
				{
					id: COLUMN_ACCESSORS.END_LOCATION,
					Header: t('RESOURCES_TABLE.END_LOCATION'),
					accessor: COLUMN_ACCESSORS.END_LOCATION,
					filter: 'byName',
					Cell: ({ row, value }: CellProps<any>) =>
						getLocationsSelect(
							endLocations,
							row,
							value,
							getDefaultLocation(false, row.original.resource),
							hasHomeAddress,
							hasParkingLocation,
							option => {
								const item: ParkingSpotLocation | DefaultLocation = endLocations.get(option);
								if (item.defaultLocation) {
									setResourceField(row.original.resource, option, 'end_location_type');
									setResourceField(row.original.resource, null, 'end_parking_spot_id');
								} else {
									setResourceField(
										row.original.resource,
										DeliveryBlockResourceLocationType.PARKING_SPOT,
										'end_location_type'
									);
									setResourceField(row.original.resource, option, 'end_parking_spot_id');
								}
							},
							t('DELIVERY_BLOCK_MODAL.LOCATIONS_LIST'),
							t('DELIVERY_BLOCK_MODAL.OTHER_LOCATIONS_LIST'),
							'end-location-item-text'
						)
				},
				{
					id: COLUMN_ACCESSORS.REMOVE_RESOURCE,
					accessor: COLUMN_ACCESSORS.REMOVE_RESOURCE,
					width: 60,
					Cell: ({ row }: CellProps<any>) => (
						<Button
							className="icon-trash-container"
							type="link"
							onClick={() => {
								removeResource(row.original.resource);
							}}
						>
							<BringgIcon iconName={BringgFontIcons.Trash} />
						</Button>
					)
				}
			]
		].map(col => ({
			...col,
			Header: (
				<div data-test-id="header-container" onClick={() => onHeaderClick(col.id)}>
					{sortByColumn && col.id === sortByColumn.id && (
						<BringgIcon
							iconName={sortByColumn.desc_direction ? BringgFontIcons.ArrowDown : BringgFontIcons.ArrowUp}
						/>
					)}
					<span>{col.Header}</span>
				</div>
			)
		}));
	}, [
		resources,
		selectedDriverIds,
		selectedVehiclesIds,
		deliveryBlockTimeRange.startTime,
		deliveryBlockTimeRange.endTime,
		sortByColumn
	]);

	return {
		getLocations,
		getTableData,
		getTableColumns
	};
};
export default useResources;
